import {defineStore} from 'pinia';
import _ from "lodash";
import {ChartData, Color, HoveredElement, KernCijfers} from "./types";
import translate from "../../../../lib/Translate";


export const useKerncijfersStore = defineStore('kerncijfersStore', {
    state: () => ({
        kernCijfers: window.cw.kernCijfers as KernCijfers,
        containerHeight: 320,
        containerWidth: 0, //gets defined in mounted hook based on available space.
        margin: 25,
        yAxisWidth: 110,
        labelsWidth: 256,
        chartData: null as ChartData | null,
        hoveredElement: null as HoveredElement | null,
    }),
    getters: {
        years(): string[] {
            return _.takeRight(_.keys(this.kernCijfers), this.maxYears);
        },
        shownKernCijfers(): KernCijfers {
            return _.pick(this.kernCijfers, this.years.map(c => +c));
        },
        winstValuesPerYear(): Record<number, number> {
            return _.mapValues(this.shownKernCijfers, (year) => parseInt(year.winst.replace(/[^0-9-]+/g, '')));
        },
        omzetValuesPerYear(): Record<number, number> {
            return _.mapValues(this.shownKernCijfers, (year) => parseInt(year.omzet.replace(/[^0-9-]+/g, '')));
        },
        brutoMargeValuesPerYear(): Record<number, number> {
            return _.mapValues(this.shownKernCijfers, (year) => parseInt(year.bruto_marge.replace(/[^0-9-]+/g, '')));
        },
        eigenVermogenValuesPerYear(): Record<number, number> {
            return _.mapValues(this.shownKernCijfers, (year) => parseInt(year.eigen_vermogen.replace(/[^0-9-]+/g, '')));
        },
        balanstotaalValuesPerYear(): Record<number, number> {
            return _.mapValues(this.shownKernCijfers, (year) => parseInt(year.balanstotaal.replace(/[^0-9-]+/g, '')));
        },
        totaleSchuldenValuesPerYear(): Record<number, number> {
            return _.mapValues(this.shownKernCijfers, (year) => parseInt(year.totale_schulden.replace(/[^0-9-]+/g, '')));
        },
        chartHeight(): number {
            return this.containerHeight - (this.margin * 2);
        },
        chartWidth(): number {
            return this.containerWidth - this.yAxisWidth - 30;
        },
        yearWidth(): number {
            return this.years.length === 1 ? this.chartWidth : Math.abs(this.chartWidth / (this.years.length - 1));
        },
        maxYears(): number {
            return this.isMobile ? 4 : this.chartWidth <= 600 ? 6 : 10;
        },
        activeChartValues(): number[] {
            return _.flatMap(
                _.filter(
                    this.chartData, kc => {
                        return kc.isActive && !kc.hasNoValues;
                    }
                ),
                kc => {
                    return _.map(kc.valuesPerYear);
                }
            );
        },
        lowerBound(): number {
            return _.min(this.activeChartValues)!;
        },
        hasMillions(): boolean {
            return _.some(this.activeChartValues, function (v) {
                return v > 1_000_000 || v < -1_000_000;
            });
        },
        upperBound(): number {
            return _.max(this.activeChartValues)!;
        },
        valueRange(): number {
            return this.upperBound - Math.min(this.lowerBound, 0);
        },
        precision(): number {
            if (!this.valueRange)
                return 0;

            const amountOfZeros = Math.floor(Math.log10(Math.abs(this.valueRange)));
            const precisionInPowerOfTen = Math.pow(10, amountOfZeros);
            const unit = Math.ceil(this.valueRange / precisionInPowerOfTen);

            return unit * (precisionInPowerOfTen / 5);
        },
        upperAxisLimit(): number {
            const roundUp = (num: number, precision: number): number => Math.ceil(num / precision) * precision;
            
            return roundUp(this.upperBound, this.precision);
        },
        lowerAxisLimit(): number {
            const roundDown = (num: number, precision: number): number => Math.floor(num / precision) * precision;
            
            return roundDown(Math.min(this.lowerBound, 0), this.precision);
        },
        yAxisValues(): number[] {
            if (this.upperAxisLimit === null || this.upperAxisLimit === undefined)
                return [];

            return _.range(
                this.lowerAxisLimit,
                this.upperAxisLimit + this.precision,
                this.precision
            );
        },
        range(): number {
            return this.upperAxisLimit - this.lowerAxisLimit;
        },
        isMobile(): boolean {
            return window.outerWidth <= 500;
        },
    },
    actions: {
        valueToXPos(index: number): number {
            return this.years.length === 1 ? this.yearWidth / 2 : index * this.yearWidth;
        },
        valueToYPos(val: number): number {
            const heightPercentage: number = (val + Math.abs(_.min(this.yAxisValues) ?? 0)) / this.range;

            return this.chartHeight - heightPercentage * this.chartHeight;
        },
        createChartData(): void {
            this.chartData = {
                winst: {
                    valuesPerYear: this.winstValuesPerYear,
                    isActive: true,
                    label: translate('label_kerncijfers_winst'),
                    color: Color.RED,
                    hasNoValues: _.every(this.kernCijfers, year => year.winst === "")
                },
                omzet: {
                    valuesPerYear: this.omzetValuesPerYear,
                    isActive: true,
                    label: translate('label_kerncijfers_omzet'),
                    color: Color.YELLOW,
                    hasNoValues: _.every(this.kernCijfers, year => year.omzet === "")
                },
                brutoMarge: {
                    valuesPerYear: this.brutoMargeValuesPerYear,
                    isActive: true,
                    label: translate('label_kerncijfers_brutomarge'),
                    color: Color.LILA,
                    hasNoValues: _.every(this.kernCijfers, year => year.bruto_marge === "")
                },
                eigenVermogen: {
                    valuesPerYear: this.eigenVermogenValuesPerYear,
                    isActive: true,
                    label: translate('label_kerncijfers_eigen_vermogen'),
                    color: Color.TEAL,
                    hasNoValues: _.every(this.kernCijfers, year => year.eigen_vermogen === "")
                },
                balansTotaal: {
                    valuesPerYear: this.balanstotaalValuesPerYear,
                    isActive: true,
                    label: translate('balans_totaal'),
                    color: Color.CYAN,
                    hasNoValues: _.every(this.kernCijfers, year => year.balanstotaal === "")
                },
                totaleSchulden: {
                    valuesPerYear: this.totaleSchuldenValuesPerYear,
                    isActive: true,
                    label: translate('schulden'),
                    color: Color.PINK,
                    hasNoValues: _.every(this.kernCijfers, year => year.totale_schulden === "")
                }
            };
        }
    }
});